import { useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import lrqaLogo from "../assets/clients/LRQA logo.jpg";

// ================= GOOGLE APPS SCRIPT SETTINGS =================
const API_URL =
  "https://script.google.com/macros/s/AKfycbwEY7K2RzMree5K2sy5jxSqJhl54zohl3wgfohhrgpAyADXKYXk6TxNkcC7_zoCo_8I/exec";
const TOKEN = "lrqa-Xk7pQ2mZ9vR4";

// ================= LOOK & FEEL (copied from the client's Word report) =================
// Font: Calibri.  Sizes taken from the client's file:
//   title 14pt | tables 10pt | inspection text 11pt
// If the client wants everything at 12pt, change PT10 / PT11 to "16px".
const FONT = "Calibri, Carlito, 'Segoe UI', Arial, sans-serif";
const PT10 = "13.33px";
const PT11 = "14.67px";
const PT14 = "18.67px";
const SHADE = "#E7F5F0"; // light green used for every heading bar in the client's report
const LINE = "1px solid #000";
const CONTENT_W_PX = 681; // 7.1 inch table width used in the client's report

// ================= FILE NAME =================
// Client's name:  "30 & 01.07.2026 Kankan Engg VASAI IVR"
//                  = visit dates + supplier short name + site location + IVR
const buildFileName = (f) =>
  [f.visitDates, f.supplierShortName, f.siteLocation, "IVR"]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ");

// Every character that is not a letter/number becomes "_"  ->  30___01_07_2026_Kankan_Engg_VASAI_IVR
const safeFileName = (f) => buildFileName(f).replace(/[^A-Za-z0-9]/g, "_");

// ================= HELPERS =================
const lines = (t) =>
  (t || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

// Shrinks photos so the saved report stays small
const compressImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1000 / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ data: canvas.toDataURL("image/jpeg", 0.7), caption: "" });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Row factories (each call returns a NEW object)
const createEquipmentRow = () => ({ poItemNo: "", itemDescription: "", poQty: "", offeredQty: "", activities: "" });
const createRefDocRow = () => ({ document: "", revision: "" });
const createAttendeeRow = () => ({ company: "", name: "", position: "" });
const createSectionRow = () => ({ heading: "", points: "" });
const createMeasuringRow = () => ({ srNo: "", instrumentName: "", instrumentSrNo: "", calibrationValidity: "", remark: "" });
const createTimeExpenseRow = () => ({ visitDate: "", inspectionReportingHours: "", travelHours: "", totalHours: "", roundtripDistance: "", distanceUnit: "kmtrs.", expenseDetails: "" });

const emptyForm = () => ({
  // HEADER
  projectName: "",
  lrqaRefNo: "",
  client: "",
  clientRefNo: "",
  purchaseOrder: "",
  reportNo: "",
  inspectorName: "",
  visitDates: "",
  supplierSubSupplier: "",
  inspectionSiteAddress: "",
  supplierContactName: "",
  supplierContactNumber: "",

  // FILE NAME PARTS  (e.g. "Kankan Engg" + "VASAI")
  supplierShortName: "",
  siteLocation: "",

  equipment: [createEquipmentRow()],
  referenceDocuments: [createRefDocRow(), createRefDocRow(), createRefDocRow()],

  // 1. SCOPE OF VISIT
  scopeText: "",
  scopeOfVisit: [createAttendeeRow(), createAttendeeRow()],

  // 2. HEALTH & SAFETY
  healthSafety: "",

  // 3. DETAIL OF INSPECTION
  inspectionIntro: "",
  inspectionSections: [createSectionRow()],

  // 4. MEASURING EQUIPMENT
  measuringEquipment: [createMeasuringRow()],

  // 5. AREAS OF CONCERNS (one point per line)
  areasOfConcerns: "",

  // 6. NCR
  ncrIssued: "NO",
  ncrNumbers: "",

  // 7. MDR
  mdrStatus: "",

  // 8. PO DELIVERY
  poDeliveryStatus: {
    estimatedDateRelease: "",
    inspectionReleaseIssued: "NO",
    partialPoItemsReleased: "NO",
    completePoReleased: "NO",
  },

  // 9. PHOTOS
  photos: [],

  // 10. TIME AND EXPENSE
  timeExpense: [createTimeExpenseRow()],

  inspectorSignature: "",
  coordinatorReviewer: "",
});

// Makes older saved reports open safely in the new layout
const normalize = (data) => {
  const base = emptyForm();
  const out = { ...base, ...data };
  out.poDeliveryStatus = { ...base.poDeliveryStatus, ...(data.poDeliveryStatus || {}) };
  const fix = (key, factory) => {
    out[key] = (Array.isArray(out[key]) && out[key].length ? out[key] : [factory()]).map((r) => ({ ...factory(), ...r }));
  };
  fix("equipment", createEquipmentRow);
  fix("referenceDocuments", createRefDocRow);
  fix("scopeOfVisit", createAttendeeRow);
  fix("inspectionSections", createSectionRow);
  fix("measuringEquipment", createMeasuringRow);
  fix("timeExpense", createTimeExpenseRow);
  if (!Array.isArray(out.photos)) out.photos = [];
  return out;
};

// ================= SMALL PREVIEW PIECES =================
const cell = { border: LINE, padding: "3px 6px", fontSize: PT10, verticalAlign: "middle" };
const cellB = { ...cell, fontWeight: "bold" };
const tableStyle = { width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontFamily: FONT };

const Box = ({ checked }) => (
  <span
    style={{
      display: "inline-block",
      width: 11,
      height: 11,
      border: LINE,
      marginRight: 4,
      verticalAlign: "middle",
      textAlign: "center",
      lineHeight: "10px",
      fontSize: 10,
      fontWeight: "bold",
      background: "#fff",
    }}
  >
    {checked ? "✕" : ""}
  </span>
);

const YesNo = ({ value }) => (
  <span style={{ whiteSpace: "nowrap" }}>
    <Box checked={value === "YES"} />
    YES&nbsp;&nbsp;
    <Box checked={value === "NO"} />
    NO
  </span>
);

// One block of the report. The PDF is built block by block so nothing is cut in half.
const Block = ({ children, gap = true }) => (
  <div data-pdf-block="1" style={{ paddingBottom: gap ? 10 : 0, background: "#fff" }}>
    {children}
  </div>
);

// Shaded heading bar with border on all sides
const Bar = ({ children, center, noBottom }) => (
  <div
    style={{
      background: SHADE,
      border: LINE,
      borderBottom: noBottom ? "none" : LINE,
      padding: "3px 6px",
      fontSize: PT10,
      fontWeight: "bold",
      textAlign: center ? "center" : "left",
    }}
  >
    {children}
  </div>
);

// Box that only has left/right (and optionally top/bottom) borders so it can continue over pages
const sides = (top, bottom) => ({
  borderLeft: LINE,
  borderRight: LINE,
  borderTop: top ? LINE : "none",
  borderBottom: bottom ? LINE : "none",
  padding: "4px 8px",
});

// Document cell: "PO NO: VWSPL/..." -> label normal, value bold (as in client's report)
const RefDoc = ({ text }) => {
  const m = /^([^:=]*[:=]\s*)(.*)$/.exec(text || "");
  if (!m) return <>{text}</>;
  return (
    <>
      {m[1]}
      <b>{m[2]}</b>
    </>
  );
};

// ================= POP-UPS =================
const TOAST_STYLES = {
  success: { color: "#198754", bg: "#e8f6ee", icon: "✓" },
  error: { color: "#dc3545", bg: "#fdecee", icon: "✕" },
  warning: { color: "#e69500", bg: "#fff5e0", icon: "!" },
  info: { color: "#0d6efd", bg: "#e7f0ff", icon: "i" },
};

const PopupStyles = () => (
  <style>{`
    @keyframes lrqaFade { from { opacity: 0 } to { opacity: 1 } }
    @keyframes lrqaPop { from { opacity: 0; transform: translateY(14px) scale(.96) } to { opacity: 1; transform: none } }
    @keyframes lrqaSlide { from { opacity: 0; transform: translateX(40px) } to { opacity: 1; transform: none } }
    @keyframes lrqaBar { from { width: 100% } to { width: 0 } }
  `}</style>
);

const ToastList = ({ toasts, onClose }) => (
  <div style={{ position: "fixed", top: 20, right: 20, zIndex: 10001, display: "flex", flexDirection: "column", gap: 12, width: 340, maxWidth: "calc(100vw - 40px)" }}>
    {toasts.map((t) => {
      const st = TOAST_STYLES[t.type] || TOAST_STYLES.info;
      return (
        <div
          key={t.id}
          style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,.18)", borderLeft: `5px solid ${st.color}`, padding: "14px 36px 16px 14px", display: "flex", gap: 12, animation: "lrqaSlide .3s ease", fontFamily: "Arial, sans-serif" }}
        >
          <div style={{ flex: "0 0 32px", height: 32, borderRadius: "50%", background: st.bg, color: st.color, fontWeight: "bold", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>{st.icon}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: "bold", fontSize: 15, color: "#1f2937" }}>{t.title}</div>
            {t.message && <div style={{ fontSize: 13, color: "#555", marginTop: 3, wordBreak: "break-word" }}>{t.message}</div>}
          </div>
          <button onClick={() => onClose(t.id)} aria-label="Close" style={{ position: "absolute", top: 8, right: 10, border: "none", background: "transparent", fontSize: 16, color: "#999", cursor: "pointer" }}>✕</button>
          <div style={{ position: "absolute", left: 0, bottom: 0, height: 3, background: st.color, opacity: 0.5, animation: "lrqaBar 4.5s linear forwards" }} />
        </div>
      );
    })}
  </div>
);

const ConfirmDialog = ({ dialog, onAnswer }) => {
  if (!dialog) return null;
  const color = dialog.danger ? "#dc3545" : "#0d6efd";
  return (
    <div
      onClick={() => onAnswer(false)}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", backdropFilter: "blur(3px)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, animation: "lrqaFade .2s ease" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, width: 420, maxWidth: "100%", padding: "28px 26px 22px", textAlign: "center", boxShadow: "0 20px 50px rgba(0,0,0,.3)", animation: "lrqaPop .25s ease", fontFamily: "Arial, sans-serif" }}
      >
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: dialog.danger ? "#fdecee" : "#e7f0ff", color, fontSize: 30, fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          {dialog.danger ? "🗑" : "?"}
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: 20, color: "#1f2937" }}>{dialog.title}</h3>
        {dialog.message && (
          <div style={{ background: "#f3f4f6", borderRadius: 8, padding: "8px 12px", fontSize: 14, color: "#374151", wordBreak: "break-word", margin: "10px 0" }}>{dialog.message}</div>
        )}
        {dialog.note && <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>{dialog.note}</p>}
        <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
          <button onClick={() => onAnswer(false)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 15, fontWeight: "bold", cursor: "pointer" }}>
            {dialog.cancelText || "Cancel"}
          </button>
          <button autoFocus onClick={() => onAnswer(true)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none", background: color, color: "#fff", fontSize: 15, fontWeight: "bold", cursor: "pointer" }}>
            {dialog.confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= MAIN COMPONENT =================
const LRQAInspectionReport = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState(emptyForm());
  const pageRef = useRef();

  const [savedReports, setSavedReports] = useState([]);
  const [busy, setBusy] = useState(false);

  // ---------- pop-ups (toast + confirm dialog) ----------
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const dialogResolver = useRef(null);

  const notify = (type, title, message) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, title, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4500);
  };
  const closeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const askConfirm = (opts) =>
    new Promise((resolve) => {
      dialogResolver.current = resolve;
      setDialog(opts);
    });
  const answerDialog = (value) => {
    setDialog(null);
    if (dialogResolver.current) dialogResolver.current(value);
    dialogResolver.current = null;
  };

  const fileName = buildFileName(form); // readable
  const fileKey = safeFileName(form); // used for saving

  // ---------- generic helpers ----------
  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const updateTable = (section, index, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  };

  const addRow = (section, factory) => {
    setForm((prev) => ({ ...prev, [section]: [...prev[section], factory()] }));
  };

  const removeRow = (section, index) => {
    setForm((prev) =>
      prev[section].length > 1 ? { ...prev, [section]: prev[section].filter((_, i) => i !== index) } : prev
    );
  };

  const updatePoDelivery = (key, value) => {
    setForm((prev) => ({ ...prev, poDeliveryStatus: { ...prev.poDeliveryStatus, [key]: value } }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    Promise.all(files.map(compressImage)).then((photos) => {
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...photos] }));
    });
    e.target.value = "";
  };

  const readSignature = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setField(key, ev.target.result);
    reader.readAsDataURL(file);
  };

  // ================= SUBMIT / LOAD (GOOGLE DRIVE) =================
  const submitReport = async () => {
    if (!fileName || !form.visitDates.trim()) {
      notify("warning", "Missing details", "Please fill VISIT DATES, Supplier Short Name and Site Location first. They make the report name.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        // fileName = name the report must be saved with in Google Drive
        body: JSON.stringify({ token: TOKEN, fileName: fileKey, form: { ...form, fileName: fileKey } }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed");
      notify("success", "Report submitted", `Saved as: ${fileKey}`);
    } catch (err) {
      notify("error", "Submission failed", "Please check your internet and try again.");
    }
    setBusy(false);
  };

  const loadReportList = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}?token=${TOKEN}`);
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error(data.error || "Failed");
      setSavedReports(data);
      if (data.length === 0) notify("info", "No saved reports", "There are no reports saved yet.");
    } catch (err) {
      notify("error", "Could not load reports", "Please try again.");
    }
    setBusy(false);
  };

  const openReport = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}?action=get&id=${id}&token=${TOKEN}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setForm(normalize(data));
      window.scrollTo(0, 0);
    } catch (err) {
      notify("error", "Could not open report", "Please try again.");
    }
    setBusy(false);
  };

  // Delete a saved report (moves the file to Google Drive trash)
  const deleteReport = async (id, name) => {
    const ok = await askConfirm({
      title: "Delete this report?",
      message: name,
      note: "The file will be moved to the Google Drive trash.",
      confirmText: "Yes, Delete",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}?action=delete&id=${id}&token=${TOKEN}`);
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed");
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
      notify("success", "Report deleted", name);
    } catch (err) {
      notify("error", "Could not delete report", "Please try again.");
    }
    setBusy(false);
  };

  // ================= PDF (US Letter, logo + form number on every page) =================
  const generatePDF = async () => {
    setBusy(true);
    try {
      const PAGE_W = 215.9;
      const PAGE_H = 279.4;
      const CONTENT_W = 180.3; // 7.1 inch, same as the client's tables
      const X = (PAGE_W - CONTENT_W) / 2;
      const TOP = 28;
      const BOTTOM = PAGE_H - 20;

      const pdf = new jsPDF("p", "mm", "letter");
      const logo = await loadImage(lrqaLogo);
      const logoH = 14;
      const logoW = (logo.width / logo.height) * logoH;

      const drawChrome = () => {
        pdf.addImage(logo, "JPEG", X, 7, logoW, logoH);
        pdf.setFont("times", "normal");
        pdf.setFontSize(10);
        pdf.text("Form No CD 16 Rev 15", 25.4, PAGE_H - 12);
      };

      let y = TOP;
      drawChrome();
      const newPage = () => {
        pdf.addPage();
        drawChrome();
        y = TOP;
      };

      const blocks = pageRef.current.querySelectorAll("[data-pdf-block]");
      for (const block of blocks) {
        const canvas = await html2canvas(block, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });
        const h = (canvas.height / canvas.width) * CONTENT_W;

        if (h <= BOTTOM - TOP) {
          if (y + h > BOTTOM) newPage();
          pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", X, y, CONTENT_W, h);
          y += h;
        } else {
          // very tall block: slice it across pages
          let offset = 0;
          while (offset < h - 0.1) {
            if (BOTTOM - y < 25) newPage();
            const slice = Math.min(BOTTOM - y, h - offset);
            const sc = document.createElement("canvas");
            sc.width = canvas.width;
            sc.height = Math.max(1, Math.round((slice / h) * canvas.height));
            sc.getContext("2d").drawImage(
              canvas,
              0,
              Math.round((offset / h) * canvas.height),
              canvas.width,
              sc.height,
              0,
              0,
              canvas.width,
              sc.height
            );
            pdf.addImage(sc.toDataURL("image/jpeg", 0.92), "JPEG", X, y, CONTENT_W, slice);
            y += slice;
            offset += slice;
            if (offset < h - 0.1) newPage();
          }
        }
      }

      pdf.save(`${fileKey || "LRQA_Inspection_Report"}.pdf`);
    } catch (err) {
      console.error(err);
      notify("error", "PDF failed", "Could not create the PDF. Please try again.");
    }
    setBusy(false);
  };

  // ================= FORM UI HELPERS =================
  const input = { width: "100%", padding: "8px", boxSizing: "border-box" };
  const greenBtn = { padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" };
  const redBtn = { padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" };
  const rowStyle = { display: "flex", gap: "10px", marginBottom: "5px", alignItems: "center" };
  const headRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "20px" };

  // plain function (not a component) so inputs keep focus while typing
  const field = (label, k) => (
    <div key={k}>
      <label>{label}</label>
      <input value={form[k]} onChange={(e) => setField(k, e.target.value)} style={input} />
    </div>
  );

  // ================= RENDER =================
  const photoRows = [];
  for (let i = 0; i < form.photos.length; i += 2) photoRows.push(form.photos.slice(i, i + 2));

  const sectionsToShow = form.inspectionSections.filter((s) => s.heading.trim() || s.points.trim());
  const areaPoints = lines(form.areasOfConcerns);
  const po = form.poDeliveryStatus;

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      <PopupStyles />
      <ToastList toasts={toasts} onClose={closeToast} />
      <ConfirmDialog dialog={dialog} onAnswer={answerDialog} />
      {!loggedIn ? (
        <div style={{ maxWidth: 350, margin: "0 auto" }}>
          <h2>Login</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email === "qnexservices@gmail.com" && password === "Qnexa@123") {
                setLoggedIn(true);
                setError("");
              } else {
                setError("Invalid Credentials");
              }
            }}
          >
            <input
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%", padding: 8, marginBottom: 10, border: "1px solid #ccc", boxSizing: "border-box" }}
            />
            <input
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%", padding: 8, marginBottom: 10, border: "1px solid #ccc", boxSizing: "border-box" }}
            />
            {error && <p style={{ color: "red" }}>{error}</p>}
            <button style={{ width: "100%", padding: 10, background: "#007bff", color: "#fff", border: "none" }}>Login</button>
          </form>
        </div>
      ) : (
        <>
          {/* ================= PREVIEW (same layout as client's Word report) ================= */}
          <div
            style={{
              width: "8.5in",
              background: "white",
              padding: "0.4in 0.7in 0.6in 0.7in",
              margin: "auto",
              boxShadow: "0 0 5px rgba(0,0,0,0.3)",
              boxSizing: "border-box",
              fontFamily: FONT,
              color: "#000",
            }}
          >
            {/* logo is drawn on every page of the PDF, shown here once for the preview */}
            <img src={lrqaLogo} alt="LRQA" style={{ height: "53px", display: "block", marginBottom: 8 }} />

            <div ref={pageRef} style={{ width: CONTENT_W_PX, fontFamily: FONT, background: "#fff" }}>
              {/* ---------- TITLE + HEADER TABLE ---------- */}
              <Block>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "21%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td colSpan={4} style={{ ...cellB, background: SHADE, textAlign: "center", fontSize: PT14, padding: "4px" }}>
                        INSPECTION REPORT
                      </td>
                    </tr>
                    <tr style={{ height: 52 }}>
                      <td style={cellB}>PROJECT NAME:</td>
                      <td style={cell}>{form.projectName}</td>
                      <td style={cellB}>LRQA REFERENCE NO:</td>
                      <td style={cell}>{form.lrqaRefNo}</td>
                    </tr>
                    <tr style={{ height: 36 }}>
                      <td style={cellB}>CLIENT:</td>
                      <td style={cell}>{form.client}</td>
                      <td style={cellB}>CLIENT REFERENCE NO:</td>
                      <td style={cell}>{form.clientRefNo}</td>
                    </tr>
                    <tr style={{ height: 36 }}>
                      <td style={cellB}>PURCHASE ORDER:</td>
                      <td style={cell}>{form.purchaseOrder}</td>
                      <td style={cellB}>REPORT NO:</td>
                      <td style={cell}>{form.reportNo}</td>
                    </tr>
                    <tr style={{ height: 26 }}>
                      <td style={cellB}>INSPECTOR NAME</td>
                      <td style={cell}>{form.inspectorName}</td>
                      <td style={cellB}>VISIT DATES:</td>
                      <td style={cell}>{form.visitDates}</td>
                    </tr>
                  </tbody>
                </table>
              </Block>

              {/* ---------- SUPPLIER / SITE + EQUIPMENT ---------- */}
              <Block>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "13.5%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "23.5%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td colSpan={3} style={{ ...cellB, background: SHADE }}>SUPPLIER/SUB-SUPPLIER</td>
                      <td colSpan={2} style={{ ...cellB, background: SHADE }}>INSPECTION SITE ADDRESS</td>
                    </tr>
                    <tr style={{ height: 40 }}>
                      <td colSpan={3} style={cell}>{form.supplierSubSupplier}</td>
                      <td colSpan={2} style={cell}>{form.inspectionSiteAddress}</td>
                    </tr>
                    <tr>
                      <td colSpan={3} style={{ ...cellB, background: SHADE }}>SUPPLIER CONTACT NAME</td>
                      <td colSpan={2} style={{ ...cellB, background: SHADE }}>SUPPLIER CONTACT NUMBER/ EMAIL</td>
                    </tr>
                    <tr style={{ height: 26 }}>
                      <td colSpan={3} style={cell}>{form.supplierContactName}</td>
                      <td colSpan={2} style={cell}>{form.supplierContactNumber}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} style={{ ...cellB, background: SHADE }}>EQUIPMENT DESCRIPTION</td>
                    </tr>
                    <tr>
                      <td style={{ ...cellB, textAlign: "center" }}>PO ITEM NO</td>
                      <td style={{ ...cellB, textAlign: "center" }}>ITEM DESCRIPTION</td>
                      <td style={{ ...cellB, textAlign: "center" }}>PO Qty.</td>
                      <td style={{ ...cellB, textAlign: "center" }}>Offered Qty.</td>
                      <td style={{ ...cellB, textAlign: "center" }}>Activities</td>
                    </tr>
                    {form.equipment.map((row, i) => (
                      <tr key={i} style={{ height: 44 }}>
                        <td style={{ ...cell, textAlign: "center" }}>{row.poItemNo}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.itemDescription}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.poQty}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.offeredQty}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.activities}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>

              {/* ---------- REFERENCE DOCUMENTS ---------- */}
              <Block>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "88%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td style={cellB}>REFERENCE DOCUMENTS (PO / SPECIFICATIONS / DRAWINGS, ETC.)</td>
                      <td style={cellB}>REVISION</td>
                    </tr>
                    {form.referenceDocuments.map((row, i) => (
                      <tr key={i} style={{ height: 20 }}>
                        <td style={cell}><RefDoc text={row.document} /></td>
                        <td style={cellB}>{row.revision}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>

              {/* ---------- INSPECTION ACTIVITY / 1. SCOPE / 2. H&S ---------- */}
              <Block>
                <Bar center>INSPECTION ACTIVITY</Bar>
                <Bar noBottom>1. SCOPE OF VISIT</Bar>
                <div style={{ ...sides(true, false), fontSize: PT10, minHeight: 36 }}>{form.scopeText}</div>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "33%" }} />
                    <col style={{ width: "29%" }} />
                  </colgroup>
                  <tbody>
                    <tr><td colSpan={3} style={cellB}>Attendees:</td></tr>
                    <tr>
                      <td style={cellB}>Company</td>
                      <td style={cellB}>Name</td>
                      <td style={cellB}>Position</td>
                    </tr>
                    {form.scopeOfVisit.map((row, i) => (
                      <tr key={i}>
                        <td style={cell}>{row.company}</td>
                        <td style={cell}>{row.name}</td>
                        <td style={cell}>{row.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Bar>2. HEALTH &amp; SAFETY</Bar>
                <div style={{ ...sides(false, true), fontSize: PT10, borderBottom: LINE }}>
                  <div style={{ minHeight: 16 }}>{form.healthSafety}</div>
                  <div style={{ marginTop: 4 }}>Comments: (Please include positive and negative Health and Safety observations)</div>
                </div>
              </Block>

              {/* ---------- 3. DETAIL OF INSPECTION ---------- */}
              <Block gap={false}>
                <Bar noBottom>3. DETAIL OF INSPECTION</Bar>
                <div
                  style={{
                    ...sides(true, sectionsToShow.length === 0),
                    fontSize: PT11,
                    fontWeight: "bold",
                    minHeight: 40,
                    paddingTop: 10,
                    paddingBottom: 6,
                  }}
                >
                  {form.inspectionIntro}
                </div>
              </Block>
              {sectionsToShow.map((s, i) => {
                const last = i === sectionsToShow.length - 1;
                return (
                  <Block key={i} gap={last}>
                    <div style={{ ...sides(false, last), fontSize: PT11, paddingBottom: 8 }}>
                      {s.heading && (
                        <div style={{ fontWeight: "bold", textDecoration: "underline", marginBottom: 4 }}>{s.heading}</div>
                      )}
                      <ul style={{ margin: 0, paddingLeft: 30 }}>
                        {lines(s.points).map((p, j) => (
                          <li key={j} style={{ lineHeight: 1.5, marginBottom: 2 }}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  </Block>
                );
              })}

              {/* ---------- 4. MEASURING EQUIPMENT ---------- */}
              <Block>
                <Bar noBottom>4. DETAIL OF MEASURING EQUIPMENT AND CALIBRATION</Bar>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "31%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "21%" }} />
                    <col style={{ width: "16%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td style={cellB}>SR.NO</td>
                      <td style={cellB}>INSTRUMENT NAME</td>
                      <td style={cellB}>INSTRUMENT SR.NO.</td>
                      <td style={cellB}>Calibration Validity</td>
                      <td style={cellB}>Remark</td>
                    </tr>
                    {form.measuringEquipment.map((row, i) => (
                      <tr key={i}>
                        <td style={cellB}>{row.srNo}</td>
                        <td style={cellB}>{row.instrumentName}</td>
                        <td style={cellB}>{row.instrumentSrNo}</td>
                        <td style={cellB}>{row.calibrationValidity}</td>
                        <td style={cellB}>{row.remark}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>

              {/* ---------- 5. AREAS OF CONCERNS ---------- */}
              <Block>
                <Bar noBottom>5. AREAS OF CONCERNS</Bar>
                <div style={{ ...sides(true, true), fontSize: PT11, color: "#FF0000", minHeight: 30 }}>
                  <ul style={{ margin: 0, paddingLeft: 30 }}>
                    {areaPoints.map((p, i) => (
                      <li key={i} style={{ lineHeight: 1.5, marginBottom: 8 }}>{p}</li>
                    ))}
                  </ul>
                </div>
              </Block>

              {/* ---------- 6. NCR ---------- */}
              <Block>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "42%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "36%" }} />
                  </colgroup>
                  <tbody>
                    <tr style={{ height: 34 }}>
                      <td style={{ ...cellB, background: SHADE }}>6. NON CONFORMANCE REPORT (NCR) ISSUED</td>
                      <td style={{ ...cell, background: SHADE, textAlign: "center" }}>
                        <Box checked={form.ncrIssued === "YES"} />YES
                      </td>
                      <td style={{ ...cell, background: SHADE, textAlign: "center" }}>
                        <Box checked={form.ncrIssued === "NO"} />NO
                      </td>
                      <td style={{ ...cell, background: SHADE }}>
                        IF YES, NCR No.(s): {form.ncrIssued === "YES" ? form.ncrNumbers : ""}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Block>

              {/* ---------- 7. MDR ---------- */}
              <Block>
                <Bar noBottom>7. STATUS OF MANUFACTURERS DATA REPORT (MDR)</Bar>
                <div style={{ ...sides(true, true), fontSize: PT10, minHeight: 20 }}>{form.mdrStatus}</div>
              </Block>

              {/* ---------- 8. PO DELIVERY STATUS ---------- */}
              <Block>
                <Bar noBottom>8. PO DELIVERY STATUS</Bar>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "42%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "42%" }} />
                  </colgroup>
                  <tbody>
                    <tr style={{ height: 26 }}>
                      <td style={cell}>ESTIMATED DATE OF RELEASE</td>
                      <td style={{ ...cell, textAlign: "center" }}>
                        {po.estimatedDateRelease ? po.estimatedDateRelease : <YesNo value="" />}
                      </td>
                      <td style={cell}>IF YES, INSERT AS ATTACHMENT</td>
                    </tr>
                    <tr style={{ height: 26 }}>
                      <td style={cell}>INSPECTION RELEASE ISSUED</td>
                      <td style={{ ...cell, textAlign: "center" }}><YesNo value={po.inspectionReleaseIssued} /></td>
                      <td style={cell}></td>
                    </tr>
                    <tr style={{ height: 26 }}>
                      <td style={cell}>PARTIAL PO ITEMS RELEASED</td>
                      <td style={{ ...cell, textAlign: "center" }}><YesNo value={po.partialPoItemsReleased} /></td>
                      <td style={cell}></td>
                    </tr>
                    <tr style={{ height: 26 }}>
                      <td style={cell}>COMPLETE PO RELEASED</td>
                      <td style={{ ...cell, textAlign: "center" }}><YesNo value={po.completePoReleased} /></td>
                      <td style={cell}></td>
                    </tr>
                  </tbody>
                </table>
              </Block>

              {/* ---------- 9. PHOTOS ---------- */}
              {photoRows.length === 0 && (
                <Block>
                  <Bar>9. PHOTOS REQUIRED – INSERT PHOTO &amp; ITS DESCRIPTION BELOW</Bar>
                </Block>
              )}
              {photoRows.map((pair, r) => (
                <Block key={r} gap={r === photoRows.length - 1}>
                  {r === 0 && <Bar noBottom>9. PHOTOS REQUIRED – INSERT PHOTO &amp; ITS DESCRIPTION BELOW</Bar>}
                  <table style={tableStyle}>
                    <colgroup>
                      <col style={{ width: "50%" }} />
                      <col style={{ width: "50%" }} />
                    </colgroup>
                    <tbody>
                      <tr>
                        {[0, 1].map((c) => (
                          <td key={c} style={{ ...cell, padding: 0, height: 255 }}>
                            {pair[c] && (
                              <img
                                src={pair[c].data}
                                alt={`Photo ${r * 2 + c + 1}`}
                                style={{ width: "100%", height: 255, objectFit: "cover", display: "block" }}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        {[0, 1].map((c) => (
                          <td key={c} style={{ ...cellB, textAlign: "center", height: 20 }}>
                            {pair[c] ? pair[c].caption : ""}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </Block>
              ))}

              {/* ---------- 10. TIME AND EXPENSE ---------- */}
              <Block>
                <Bar noBottom>10. TIME AND EXPENSE</Bar>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td style={{ ...cellB, textAlign: "center" }}>Visit Date</td>
                      <td style={{ ...cellB, textAlign: "center" }}>Inspection &amp; Reporting Hours</td>
                      <td style={{ ...cellB, textAlign: "center" }}>Travel Hours</td>
                      <td style={{ ...cellB, textAlign: "center" }}>Total Hours</td>
                      <td style={{ ...cellB, textAlign: "center" }}>
                        Roundtrip Distance <br />(km or mile, please specify)
                      </td>
                      <td style={{ ...cellB, textAlign: "center" }}>Expense Details</td>
                    </tr>
                    {form.timeExpense.map((row, i) => (
                      <tr key={i}>
                        <td style={{ ...cell, textAlign: "center" }}>{row.visitDate}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.inspectionReportingHours}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.travelHours}</td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.totalHours}</td>
                        <td style={{ ...cell, textAlign: "center" }}>
                          {row.roundtripDistance} {row.roundtripDistance ? row.distanceUnit : ""}
                        </td>
                        <td style={{ ...cell, textAlign: "center" }}>{row.expenseDetails}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Block>

              {/* ---------- SIGNATURES + DISCLAIMER ---------- */}
              <Block>
                <table style={tableStyle}>
                  <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "50%" }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td style={cellB}>Inspector Name/Signature/ Stamp</td>
                      <td style={cellB}>Coordinator/Reviewer of Report</td>
                    </tr>
                    <tr style={{ height: 80 }}>
                      <td style={{ ...cell, verticalAlign: "top" }}>
                        {form.inspectorSignature && (
                          <img src={form.inspectorSignature} alt="Inspector Signature" style={{ maxHeight: 55, maxWidth: 200, display: "block" }} />
                        )}
                        <div>{form.inspectorName}</div>
                      </td>
                      <td style={{ ...cell, verticalAlign: "top" }}>
                        {form.coordinatorReviewer && (
                          <img src={form.coordinatorReviewer} alt="Coordinator Signature" style={{ maxHeight: 55, maxWidth: 200, display: "block" }} />
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ marginTop: 16, fontSize: "12px", textAlign: "justify", lineHeight: 1.3 }}>
                  LRQA, its subsidiaries and affiliates and their respective officers, employees or agents are, individually and collectively, referred to in this clause as ‘LRQA’. LRQA assumes no responsibility and shall not be liable to any person for any loss, damage or expense caused by reliance on the information or advice in this document or howsoever provided, unless that person has signed a contract with the relevant LRQA entity for the provision of this information or advice and in that case any responsibility or liability is exclusively on the terms and conditions set out in that contract.
                </div>
              </Block>
            </div>
          </div>

          {/* ================= FORM INPUTS ================= */}
          <div style={{ marginTop: "40px", padding: "20px", backgroundColor: "#f5f5f5" }}>
            <h2>Fill Report Details</h2>

            {/* FILE NAME */}
            <div style={{ background: "#fff", border: "1px solid #ccc", padding: 12, marginBottom: 20 }}>
              <h3 style={{ marginTop: 0 }}>Report File Name</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
                {field("SUPPLIER SHORT NAME (e.g. Kankan Engg):", "supplierShortName")}
                {field("SITE LOCATION (e.g. VASAI):", "siteLocation")}
              </div>
              <p style={{ marginBottom: 0 }}>
                Name = Visit Dates + Supplier Short Name + Site Location + IVR<br />
                <b>{fileName || "(fill Visit Dates, Supplier Short Name and Site Location)"}</b>
                <br />
                Saved as: <b>{fileKey || "-"}</b>
              </p>
            </div>

            {/* HEADER FIELDS */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px" }}>
              {field("PROJECT NAME:", "projectName")}
              {field("LRQA REFERENCE NO:", "lrqaRefNo")}
              {field("CLIENT:", "client")}
              {field("CLIENT REFERENCE NO:", "clientRefNo")}
              {field("PURCHASE ORDER:", "purchaseOrder")}
              {field("REPORT NO:", "reportNo")}
              {field("INSPECTOR NAME:", "inspectorName")}
              {field("VISIT DATES (e.g. 30 & 01.07.2026):", "visitDates")}
              {field("SUPPLIER/SUB-SUPPLIER:", "supplierSubSupplier")}
              {field("INSPECTION SITE ADDRESS:", "inspectionSiteAddress")}
              {field("SUPPLIER CONTACT NAME:", "supplierContactName")}
              {field("SUPPLIER CONTACT NUMBER/EMAIL:", "supplierContactNumber")}
            </div>

            {/* EQUIPMENT DESCRIPTION */}
            <div style={headRow}>
              <h3 style={{ margin: 0 }}>EQUIPMENT DESCRIPTION</h3>
              <button onClick={() => addRow("equipment", createEquipmentRow)} style={greenBtn}>+ Add Row</button>
            </div>
            {form.equipment.map((row, i) => (
              <div key={i} style={rowStyle}>
                <input placeholder="PO ITEM NO" value={row.poItemNo} onChange={(e) => updateTable("equipment", i, "poItemNo", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="ITEM DESCRIPTION" value={row.itemDescription} onChange={(e) => updateTable("equipment", i, "itemDescription", e.target.value)} style={{ flex: 3, padding: 8 }} />
                <input placeholder="PO Qty." value={row.poQty} onChange={(e) => updateTable("equipment", i, "poQty", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Offered Qty." value={row.offeredQty} onChange={(e) => updateTable("equipment", i, "offeredQty", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Activities" value={row.activities} onChange={(e) => updateTable("equipment", i, "activities", e.target.value)} style={{ flex: 2, padding: 8 }} />
                {form.equipment.length > 1 && <button onClick={() => removeRow("equipment", i)} style={redBtn}>✕</button>}
              </div>
            ))}

            {/* REFERENCE DOCUMENTS */}
            <div style={headRow}>
              <h3 style={{ margin: 0 }}>REFERENCE DOCUMENTS</h3>
              <button onClick={() => addRow("referenceDocuments", createRefDocRow)} style={greenBtn}>+ Add Row</button>
            </div>
            {form.referenceDocuments.map((row, i) => (
              <div key={i} style={rowStyle}>
                <input placeholder="e.g. PO NO: VWSPL/40093 DT:04.02.2026." value={row.document} onChange={(e) => updateTable("referenceDocuments", i, "document", e.target.value)} style={{ flex: 4, padding: 8 }} />
                <input placeholder="REVISION" value={row.revision} onChange={(e) => updateTable("referenceDocuments", i, "revision", e.target.value)} style={{ flex: 1, padding: 8 }} />
                {form.referenceDocuments.length > 1 && <button onClick={() => removeRow("referenceDocuments", i)} style={redBtn}>✕</button>}
              </div>
            ))}

            {/* 1. SCOPE OF VISIT */}
            <h3 style={{ marginTop: 20 }}>1. SCOPE OF VISIT</h3>
            <input
              placeholder="e.g. As per QAP: VISUAL & DIMENSION, DFT, PMI & RUN TRIAL."
              value={form.scopeText}
              onChange={(e) => setField("scopeText", e.target.value)}
              style={{ ...input, marginBottom: 10 }}
            />
            <div style={{ ...headRow, marginTop: 0 }}>
              <b>Attendees</b>
              <button onClick={() => addRow("scopeOfVisit", createAttendeeRow)} style={greenBtn}>+ Add Row</button>
            </div>
            {form.scopeOfVisit.map((row, i) => (
              <div key={i} style={rowStyle}>
                <input placeholder="Company" value={row.company} onChange={(e) => updateTable("scopeOfVisit", i, "company", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Name" value={row.name} onChange={(e) => updateTable("scopeOfVisit", i, "name", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Position" value={row.position} onChange={(e) => updateTable("scopeOfVisit", i, "position", e.target.value)} style={{ flex: 1, padding: 8 }} />
                {form.scopeOfVisit.length > 1 && <button onClick={() => removeRow("scopeOfVisit", i)} style={redBtn}>✕</button>}
              </div>
            ))}

            {/* 2. HEALTH & SAFETY */}
            <h3 style={{ marginTop: 20 }}>2. HEALTH &amp; SAFETY</h3>
            <textarea rows={2} value={form.healthSafety} onChange={(e) => setField("healthSafety", e.target.value)} style={input} />

            {/* 3. DETAIL OF INSPECTION */}
            <h3 style={{ marginTop: 20 }}>3. DETAIL OF INSPECTION</h3>
            <label>Opening line (bold) - e.g. "Visited at Mr. ... on dated ..., Activities were performed as below,"</label>
            <textarea rows={2} value={form.inspectionIntro} onChange={(e) => setField("inspectionIntro", e.target.value)} style={{ ...input, marginBottom: 10 }} />
            <div style={{ ...headRow, marginTop: 0 }}>
              <b>Headings with bullet points</b>
              <button onClick={() => addRow("inspectionSections", createSectionRow)} style={greenBtn}>+ Add Heading</button>
            </div>
            {form.inspectionSections.map((row, i) => (
              <div key={i} style={{ background: "#fff", border: "1px solid #ddd", padding: 8, marginBottom: 8 }}>
                <div style={rowStyle}>
                  <input placeholder="Heading e.g. VISUAL & DIMENSION:" value={row.heading} onChange={(e) => updateTable("inspectionSections", i, "heading", e.target.value)} style={{ flex: 1, padding: 8 }} />
                  {form.inspectionSections.length > 1 && <button onClick={() => removeRow("inspectionSections", i)} style={redBtn}>✕</button>}
                </div>
                <textarea
                  rows={3}
                  placeholder="Bullet points - one point per line"
                  value={row.points}
                  onChange={(e) => updateTable("inspectionSections", i, "points", e.target.value)}
                  style={input}
                />
              </div>
            ))}

            {/* 4. MEASURING EQUIPMENT */}
            <div style={headRow}>
              <h3 style={{ margin: 0 }}>4. MEASURING EQUIPMENT</h3>
              <button onClick={() => addRow("measuringEquipment", createMeasuringRow)} style={greenBtn}>+ Add Row</button>
            </div>
            {form.measuringEquipment.map((row, i) => (
              <div key={i} style={rowStyle}>
                <input placeholder="SR.NO" value={row.srNo} onChange={(e) => updateTable("measuringEquipment", i, "srNo", e.target.value)} style={{ width: 60, padding: 8 }} />
                <input placeholder="INSTRUMENT NAME" value={row.instrumentName} onChange={(e) => updateTable("measuringEquipment", i, "instrumentName", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="INSTRUMENT SR.NO" value={row.instrumentSrNo} onChange={(e) => updateTable("measuringEquipment", i, "instrumentSrNo", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Calibration Validity" value={row.calibrationValidity} onChange={(e) => updateTable("measuringEquipment", i, "calibrationValidity", e.target.value)} style={{ flex: 1, padding: 8 }} />
                <input placeholder="Remark" value={row.remark} onChange={(e) => updateTable("measuringEquipment", i, "remark", e.target.value)} style={{ flex: 1, padding: 8 }} />
                {form.measuringEquipment.length > 1 && <button onClick={() => removeRow("measuringEquipment", i)} style={redBtn}>✕</button>}
              </div>
            ))}

            {/* 5. AREAS OF CONCERNS */}
            <h3 style={{ marginTop: 20 }}>5. AREAS OF CONCERNS (one point per line)</h3>
            <textarea rows={4} value={form.areasOfConcerns} onChange={(e) => setField("areasOfConcerns", e.target.value)} style={input} />

            {/* 6. NCR */}
            <h3 style={{ marginTop: 20 }}>6. NON CONFORMANCE REPORT (NCR)</h3>
            <div style={{ display: "flex", gap: 20, marginBottom: 10 }}>
              <label><input type="radio" checked={form.ncrIssued === "NO"} onChange={() => setField("ncrIssued", "NO")} /> NO</label>
              <label><input type="radio" checked={form.ncrIssued === "YES"} onChange={() => setField("ncrIssued", "YES")} /> YES</label>
            </div>
            {form.ncrIssued === "YES" && (
              <input placeholder="NCR Numbers" value={form.ncrNumbers} onChange={(e) => setField("ncrNumbers", e.target.value)} style={{ ...input, marginBottom: 10 }} />
            )}

            {/* 7. MDR */}
            <h3 style={{ marginTop: 20 }}>7. STATUS OF MANUFACTURERS DATA REPORT (MDR)</h3>
            <textarea rows={2} value={form.mdrStatus} onChange={(e) => setField("mdrStatus", e.target.value)} style={input} />

            {/* 8. PO DELIVERY */}
            <h3 style={{ marginTop: 20 }}>8. PO DELIVERY STATUS</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <label>ESTIMATED DATE OF RELEASE (leave empty if none):</label>
                <input value={po.estimatedDateRelease} onChange={(e) => updatePoDelivery("estimatedDateRelease", e.target.value)} placeholder="e.g. 15.07.2026" style={input} />
              </div>
              {[
                ["INSPECTION RELEASE ISSUED:", "inspectionReleaseIssued"],
                ["PARTIAL PO ITEMS RELEASED:", "partialPoItemsReleased"],
                ["COMPLETE PO RELEASED:", "completePoReleased"],
              ].map(([label, key]) => (
                <div key={key}>
                  <label>{label}</label>
                  <select value={po[key]} onChange={(e) => updatePoDelivery(key, e.target.value)} style={input}>
                    <option value="NO">NO</option>
                    <option value="YES">YES</option>
                  </select>
                </div>
              ))}
            </div>

            {/* 9. PHOTOS */}
            <h3 style={{ marginTop: 20 }}>9. PHOTOS (2 per row - write the description under each photo)</h3>
            <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} style={{ marginBottom: 10 }} />
            {form.photos.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {form.photos.map((photo, i) => (
                  <div key={i} style={{ position: "relative" }}>
                    <img src={photo.data} alt={`Photo ${i + 1}`} style={{ width: "100%", height: 100, objectFit: "cover" }} />
                    <button
                      onClick={() => setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, idx) => idx !== i) }))}
                      style={{ ...redBtn, position: "absolute", top: 4, right: 4, padding: "2px 8px" }}
                    >
                      ✕
                    </button>
                    <input
                      placeholder="Photo description"
                      value={photo.caption}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          photos: prev.photos.map((p, idx) => (idx === i ? { ...p, caption: e.target.value } : p)),
                        }))
                      }
                      style={{ width: "100%", padding: 5, marginTop: 5, boxSizing: "border-box" }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* 10. TIME AND EXPENSE */}
            <div style={headRow}>
              <h3 style={{ margin: 0 }}>10. TIME AND EXPENSE</h3>
              <button onClick={() => addRow("timeExpense", createTimeExpenseRow)} style={greenBtn}>+ Add Row</button>
            </div>
            {form.timeExpense.map((row, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 10, alignItems: "center" }}>
                <input placeholder="Visit Date" value={row.visitDate} onChange={(e) => updateTable("timeExpense", i, "visitDate", e.target.value)} style={{ padding: 8 }} />
                <input placeholder="Inspection & Reporting (e.g. 8 hours)" value={row.inspectionReportingHours} onChange={(e) => updateTable("timeExpense", i, "inspectionReportingHours", e.target.value)} style={{ padding: 8 }} />
                <input placeholder="Travel Hours" value={row.travelHours} onChange={(e) => updateTable("timeExpense", i, "travelHours", e.target.value)} style={{ padding: 8 }} />
                <input placeholder="Total Hours" value={row.totalHours} onChange={(e) => updateTable("timeExpense", i, "totalHours", e.target.value)} style={{ padding: 8 }} />
                <div style={{ display: "flex", gap: 5 }}>
                  <input placeholder="Distance" value={row.roundtripDistance} onChange={(e) => updateTable("timeExpense", i, "roundtripDistance", e.target.value)} style={{ flex: 1, padding: 8, minWidth: 0 }} />
                  <select value={row.distanceUnit} onChange={(e) => updateTable("timeExpense", i, "distanceUnit", e.target.value)} style={{ padding: 8 }}>
                    <option value="kmtrs.">kmtrs.</option>
                    <option value="km">km</option>
                    <option value="mile">mile</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: 5 }}>
                  <input placeholder="Expense Details" value={row.expenseDetails} onChange={(e) => updateTable("timeExpense", i, "expenseDetails", e.target.value)} style={{ flex: 1, padding: 8, minWidth: 0 }} />
                  {form.timeExpense.length > 1 && <button onClick={() => removeRow("timeExpense", i)} style={redBtn}>✕</button>}
                </div>
              </div>
            ))}

            {/* SIGNATURES */}
            <h3 style={{ marginTop: 20 }}>SIGNATURES</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
              <div>
                <label>Inspector Signature:</label>
                <input type="file" accept="image/*" onChange={readSignature("inspectorSignature")} style={input} />
              </div>
              <div>
                <label>Coordinator/Reviewer Signature:</label>
                <input type="file" accept="image/*" onChange={readSignature("coordinatorReviewer")} style={input} />
              </div>
            </div>

            {/* ================= SUBMIT / LOAD / DOWNLOAD ================= */}
            <div style={{ marginTop: 20 }}>
              <button
                onClick={submitReport}
                disabled={busy}
                style={{ padding: "10px 20px", background: "#007bff", color: "white", border: "none", fontSize: 16, fontWeight: "bold", marginRight: 10, cursor: busy ? "not-allowed" : "pointer" }}
              >
                {busy ? "Please wait..." : "Submit Report"}
              </button>
              <button
                onClick={loadReportList}
                disabled={busy}
                style={{ padding: "10px 20px", background: "#6c757d", color: "white", border: "none", fontSize: 16, fontWeight: "bold", marginRight: 10, cursor: busy ? "not-allowed" : "pointer" }}
              >
                Load Saved Reports
              </button>
              <button
                onClick={generatePDF}
                disabled={busy}
                style={{ padding: "10px 20px", background: "green", color: "white", border: "none", fontSize: 16, fontWeight: "bold", cursor: busy ? "not-allowed" : "pointer" }}
              >
                Download PDF
              </button>
            </div>

            {savedReports.length > 0 && (
              <div style={{ marginTop: 15, background: "white", padding: 10, border: "1px solid #ccc" }}>
                <h4 style={{ marginTop: 0 }}>Saved Reports</h4>
                {savedReports.map((r) => (
                  <div
                    key={r.id}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #eee" }}
                  >
                    <span>
                      {r.name.replace(".json", "")} — {new Date(r.date).toLocaleString()}
                    </span>
                    <span style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => openReport(r.id)} disabled={busy} style={{ padding: "4px 12px" }}>
                        Open
                      </button>
                      <button
                        onClick={() => deleteReport(r.id, r.name.replace(".json", ""))}
                        disabled={busy}
                        style={{ padding: "4px 12px", background: "#dc3545", color: "white", border: "none", cursor: busy ? "not-allowed" : "pointer" }}
                      >
                        Delete
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LRQAInspectionReport;